'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/stores/auth.store';
import type { MessageKey } from '@/lib/i18n/messages';
import { type Locale, isSupportedLocale, normalizeLocale } from '@/lib/i18n/locale-config';
import { cachedLocaleMessages, loadLocaleMessages, primeLocaleMessages, translate, translateDictionary, type MessageDictionary } from '@/lib/i18n/client-messages';
import { inferLocaleFromBrowser } from '@/lib/i18n/infer-locale';
import { useDocumentLanguage } from '@/lib/i18n/useDocumentLanguage';
import { storage, STORAGE_KEYS } from '@/utils/storage';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, params?: Record<string, string | number>) => string;
}

/**
 * Default context — used when components are rendered outside the
 * `<LocaleProvider>` (most commonly tests). Resolves keys via the static
 * `translate('en', ...)` so components render real English copy without
 * forcing every test fixture to mount a provider.
 */
const DEFAULT_LOCALE_CONTEXT: LocaleContextType = {
  locale: 'en',
  setLocale: () => {},
  t: (key, params) => translate('en', key, params),
};

const LocaleContext = createContext<LocaleContextType>(DEFAULT_LOCALE_CONTEXT);

interface LocaleProviderProps {
  children: React.ReactNode;
  // Server-supplied locale from URL (e.g. /ka/about → "ka"). When provided,
  // it takes precedence over localStorage so SEO pages render in the URL's
  // language on the FIRST paint, not after client hydration.
  initialLocale?: Locale;
  initialMessages?: MessageDictionary;
  // Server-detected IP country (e.g. "GE"). Used only as a geo signal inside
  // the browser-inference fallback — it never overrides a saved choice, account
  // preference, or an explicit URL locale.
  geoCountry?: string | null;
}

// Extract the locale prefix from a URL path. Returns null when the path has
// no recognized locale segment (e.g. /play, /auth, /settings).
function localeFromPath(pathname: string | null): Locale | null {
  if (!pathname) return null;
  const first = pathname.replace(/^\/+/, '').split('/')[0];
  return isSupportedLocale(first) ? first : null;
}

function readStoredLocale(): { locale: Locale; hasStoredLocale: boolean } {
  const stored = storage.get<string | null>(STORAGE_KEYS.LOCALE, null);
  // Only treat the stored value as an explicit choice when it's a genuinely
  // supported locale (including regional variants such as `ka-GE` or
  // `es-MX`, `tr-TR`, `ru-RU`, `pt-BR` or `de-DE` that normalizeLocale maps to a supported base language).
  // Otherwise an unsupported value like "fr" would normalize to "en" and be
  // mistaken for a deliberate English selection, pinning the user to English
  // instead of honoring preferredLanguage / browser inference.
  const trimmed = typeof stored === 'string' ? stored.trim() : '';
  const hasStoredLocale =
    isSupportedLocale(trimmed) ||
    trimmed.toLowerCase().startsWith('ka') ||
    trimmed.toLowerCase().startsWith('es') ||
    trimmed.toLowerCase().startsWith('tr') ||
    trimmed.toLowerCase().startsWith('ru') ||
    trimmed.toLowerCase().startsWith('pt') ||
    trimmed.toLowerCase().startsWith('de');
  return {
    locale: normalizeLocale(stored),
    hasStoredLocale,
  };
}

/** Load only a newly selected language; initial localized SSR uses its own props. */
function useMessages(locale: Locale, initialLocale?: Locale, initialMessages?: MessageDictionary) {
  const [loaded, setLoaded] = useState<{ locale: Locale; dictionary: MessageDictionary | undefined }>(() => ({ locale, dictionary: cachedLocaleMessages(locale) }));
  const seed = locale === initialLocale ? initialMessages : undefined;
  useEffect(() => {
    if (seed) {
      primeLocaleMessages(locale, seed);
      return;
    }
    let active = true;
    void loadLocaleMessages(locale).then((dictionary) => {
      if (active) setLoaded({ locale, dictionary });
    }).catch(() => { /* Keep English fallback on a failed chunk; a later selection retries. */ });
    return () => { active = false; };
  }, [locale, seed]);
  return seed ?? (loaded.locale === locale ? loaded.dictionary : cachedLocaleMessages(locale));
}

export function LocaleProvider({ children, initialLocale, initialMessages, geoCountry }: LocaleProviderProps) {
  const preferredLanguage = useAuthStore((state) => state.user?.preferred_language);
  const lastSyncedPreferredLanguage = useRef<string | null | undefined>(undefined);
  // usePathname updates on every client-side navigation, so we can derive the
  // active SEO locale from the URL in real time. initialLocale only seeds the
  // very first render — after that the pathname is the source of truth.
  const pathname = usePathname();
  const pathLocale = localeFromPath(pathname);
  const localeSourceRef = useRef<'app' | 'path'>(initialLocale || pathLocale ? 'path' : 'app');
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? pathLocale ?? 'en');
  const hasHydratedRef = useRef(false);

  // Re-sync when the URL locale changes (clicking the EN/KA switcher).
  // Functional updater lets us drop `locale` from the deps — React only
  // schedules the update when the value actually differs. queueMicrotask
  // defers the dispatch off the effect tick (same pattern as the hydration
  // effect below) to satisfy the cascading-renders lint rule.
  useEffect(() => {
    if (!pathLocale) return;
    queueMicrotask(() => {
      localeSourceRef.current = 'path';
      setLocaleState((prev) => (prev !== pathLocale ? pathLocale : prev));
    });
  }, [pathLocale]);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    // If the route already supplied a locale (localized SEO pages), trust it
    // and skip the localStorage upgrade — the URL is the source of truth.
    if (initialLocale || pathLocale) return;
    const { locale: stored, hasStoredLocale } = readStoredLocale();
    const preferredLocale = isSupportedLocale(preferredLanguage) ? preferredLanguage : null;
    const nextLocale = hasStoredLocale
      ? stored
      : preferredLocale ?? inferLocaleFromBrowser(geoCountry);
    if (nextLocale !== 'en') {
      queueMicrotask(() => {
        localeSourceRef.current = 'app';
        setLocaleState(nextLocale);
      });
    }
  }, [initialLocale, pathLocale, preferredLanguage, geoCountry]);

  useEffect(() => {
    if (!hasHydratedRef.current) return;
    if (pathLocale) return;
    if (localeSourceRef.current === 'path') return;
    storage.set(STORAGE_KEYS.LOCALE, locale);
  }, [locale, pathLocale]);

  // The address still wins where it names a language.
  useDocumentLanguage(pathLocale ?? locale);

  useEffect(() => {
    if (!hasHydratedRef.current || pathLocale || localeSourceRef.current !== 'path') return;

    const { locale: storedLocale } = readStoredLocale();
    if (storedLocale === locale) {
      localeSourceRef.current = 'app';
      return;
    }

    queueMicrotask(() => {
      localeSourceRef.current = 'app';
      setLocaleState(storedLocale);
    });
  }, [locale, pathLocale]);

  useEffect(() => {
    if (!hasHydratedRef.current) return;
    if (!isSupportedLocale(preferredLanguage)) {
      lastSyncedPreferredLanguage.current = preferredLanguage;
      return;
    }
    if (pathLocale) {
      lastSyncedPreferredLanguage.current = preferredLanguage;
      return;
    }

    const { locale: storedLocale } = readStoredLocale();
    const preferredLanguageChanged = preferredLanguage !== lastSyncedPreferredLanguage.current;
    const shouldApplyPreferredLanguage =
      preferredLanguage !== locale &&
      (preferredLanguageChanged || storedLocale === preferredLanguage);

    if (shouldApplyPreferredLanguage) {
      queueMicrotask(() => {
        localeSourceRef.current = 'app';
        setLocaleState(preferredLanguage);
      });
    }
    lastSyncedPreferredLanguage.current = preferredLanguage;
  }, [locale, pathLocale, preferredLanguage]);

  const setLocale = useCallback((newLocale: Locale) => {
    localeSourceRef.current = 'app';
    setLocaleState(newLocale);
  }, []);

  const dictionary = useMessages(locale, initialLocale, initialMessages);
  const t = useCallback((key: MessageKey, params?: Record<string, string | number>) => translateDictionary(dictionary, key, params), [dictionary]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<LocaleContextType>(() => ({
    locale,
    setLocale,
    t,
  }), [locale, setLocale, t]);

  return (
    <LocaleContext.Provider value={contextValue}>
      {children}
    </LocaleContext.Provider>
  );
}

/** A locale fixed by the caller (dev previews, tests): no storage, account or URL sync, follows `locale` as it changes. */
export function FixedLocaleProvider({ locale, children }: { locale: Locale; children: React.ReactNode }) {
  const dictionary = useMessages(locale);
  const value = useMemo<LocaleContextType>(() => ({ locale, setLocale: () => {}, t: (key, params) => translateDictionary(dictionary, key, params) }), [locale, dictionary]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
